import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Pin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { displayViews } from "@/lib/viewCount";

type Drop = {
  id: string;
  title: string;
  category: string | null;
  slug: string | null;
  published_date: string | null;
  is_premium: boolean;
  is_pinned?: boolean;
};

function fmt(d: string | null): string {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/**
 * Honest "what shipped recently" timeline.
 * No fake weekly cadence: we just list the last few playbook drops.
 */
export function RecentDrops() {
  const [drops, setDrops] = useState<Drop[]>([]);
  const [views, setViews] = useState<Record<string, number>>({});

  useEffect(() => {
    let active = true;
    (async () => {
      const [contentRes, clicksRes] = await Promise.all([
        supabase
          .from("research_content")
          .select("id,title,category,slug,published_date,is_premium,is_pinned")
          .eq("is_published", true)
          .is("deleted_at", null)
          .order("is_pinned", { ascending: false })
          .order("published_date", { ascending: false, nullsFirst: false })
          .limit(6),
        supabase.from("research_content_clicks").select("content_id"),
      ]);
      if (!active) return;
      setDrops(((contentRes.data as any) || []) as Drop[]);
      const counts: Record<string, number> = {};
      ((clicksRes.data as any) || []).forEach((r: any) => {
        counts[r.content_id] = (counts[r.content_id] || 0) + 1;
      });
      setViews(counts);
    })();
    return () => { active = false; };
  }, []);

  if (!drops.length) return null;

  return (
    <div className="mt-12">
      {drops.map((d) => {
        const href = d.slug ? `/ai-investing-club/${d.slug}` : "/ai-investing-club";
        return (
          <Link
            key={d.id}
            to={href}
            className="group grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-6 gap-y-1 border-t border-[var(--sn-border)] py-4 sm:grid-cols-[112px_minmax(0,1fr)_auto]"
          >
            <span className="font-mono text-[12px] text-muted-foreground">{fmt(d.published_date)}</span>
            <span className="col-span-2 text-[16px] font-medium leading-[1.35] tracking-[-0.01em] text-foreground transition-colors group-hover:text-primary sm:col-span-1">
              {d.title}
              <ArrowRight className="ml-2 inline h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true" />
            </span>
            <span className="col-start-2 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground sm:col-start-3">
              {d.category && <span className="hidden md:inline">{d.category}</span>}
              <span>{d.is_premium ? "premium" : "free"}</span>
              {d.is_pinned && (
                <span className="inline-flex items-center gap-1"><Pin className="h-3 w-3" aria-hidden="true" /> pinned</span>
              )}
              <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" aria-hidden="true" /> {displayViews(views[d.id] || 0, d.published_date, d.id)}</span>
            </span>
          </Link>
        );
      })}
      <p className="mt-6 border-t border-[var(--sn-border)] pt-4 font-mono text-[12px] text-muted-foreground">
        No fixed schedule. Playbooks ship when they are ready. Free previews live here; members unlock the full library inside the club.
      </p>
    </div>
  );
}
